import { PASSWORD_MIN_LENGTH, checkPassword } from '@forecastify/shared';
import { useT, type MessageKey } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The password checklist, once, for the two screens that set a password.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C17's invitation page drew this and S2's sign-up screen needs the identical thing. Copying eleven
 * lines of JSX would have been faster and would have produced two checklists that agree today —
 * which is the situation `password-policy.ts` already argues against one layer down: *"the copy that
 * drifts is the one deciding what a UI offers while the server quietly disagrees."* The predicate
 * was already shared; the rendering of it was not, and a tick appearing in one place and not the
 * other is the same class of defect with a shorter fuse.
 *
 * ⚠️ **It lives in `screens/` rather than `packages/ui`, following `screen-state.tsx` and
 * `card.tsx`.** CLAUDE.md's rule is that a `packages/ui` component is domain-agnostic — it takes
 * items, columns and callbacks and knows nothing about the product. This knows about the password
 * policy and reads the catalogue, so it is a screen-level piece; pushing it down would mean passing
 * four labels in as props to avoid the i18n import, which is ceremony rather than separation.
 *
 * ⚠️ **The labels are C6's `account.password.rule.*` keys, reused rather than duplicated.** They
 * were already generic — the invitation page borrowed them, which is what made this extraction
 * mechanical.
 */

const RULE_LABEL: Readonly<Record<string, MessageKey>> = {
  length: 'account.password.rule.length',
  letters: 'account.password.rule.letters',
  digits: 'account.password.rule.digits',
  match: 'account.password.rule.match',
};

export function PasswordRules({
  password,
  confirmation,
}: {
  readonly password: string;
  readonly confirmation: string;
}) {
  const t = useT();

  return (
    /**
     * ⚠️ **`aria-live="polite"`, because a tick appearing is information and a colour is not.**
     * Somebody using a screen reader gets no signal at all from `text-emerald-600`, so the list has
     * to announce itself as it changes — and `polite` rather than `assertive` so it does not
     * interrupt every keystroke.
     */
    <ul aria-live="polite" className="flex flex-col gap-0.5">
      {Object.entries(checkPassword(password, confirmation)).map(([rule, met]) => (
        <li
          key={rule}
          className={met ? 'text-[11.5px] text-emerald-600' : 'text-muted-foreground text-[11.5px]'}
        >
          {/* ⚠️ The tick is decorative — the sentence beside it carries the meaning, and the
              `aria-live` region reads the whole item. A bare `✓`/`·` with no text would be a control
              that is invisible to anybody not looking at it. */}
          <span aria-hidden="true">{met ? '✓' : '·'}</span>{' '}
          {t(RULE_LABEL[rule] ?? 'account.password.rule.length', { min: PASSWORD_MIN_LENGTH })}
        </li>
      ))}
    </ul>
  );
}
